import React, { useState, useEffect, useCallback, ReactNode } from 'react';
import { connect, useDispatch } from 'react-redux';
import Autocomplete, { AutocompleteRenderOptionState } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { ControlProps, EnumCellProps, EnumOption, isDescriptionHidden, WithClassname } from '@jsonforms/core';
import { FilterOptionsState, FormHelperText } from '@mui/material';
import merge from 'lodash/merge';
import debounce from 'lodash/debounce';
import { secretManagerActions } from '../../../../../store/reducers/secret-manager';

const useFocus = (): [boolean, () => void, () => void] => {
  const [focused, setFocused] = useState(false);
  const onFocus = useCallback(() => setFocused(true), []);
  const onBlur = useCallback(() => setFocused(false), []);
  return [focused, onFocus, onBlur];
};

export interface WithOptionLabel {
  getOptionLabel?(option: EnumOption): string;
  renderOption?(
    props: React.HTMLAttributes<HTMLLIElement>,
    option: EnumOption,
    state: AutocompleteRenderOptionState
  ): ReactNode;
  filterOptions?(options: EnumOption[], state: FilterOptionsState<EnumOption>): EnumOption[];
  secretsList: any;
  loadingSecrets: boolean;
}

const MuiAutocomplete = (props: ControlProps & EnumCellProps & WithClassname & WithOptionLabel) => {
  const {
    description,
    errors,
    visible,
    required,
    label,
    data,
    className,
    id,
    enabled,
    uischema,
    path,
    handleChange,
    options,
    config,
    getOptionLabel,
    renderOption,
    filterOptions,
    isValid,
    secretsList,
    loadingSecrets
  } = props;

  // console.log(props, "PROPS ***");

  const appliedUiSchemaOptions = merge({}, config, uischema.options);
  const [focused, onFocus, onBlur] = useFocus();
  const [open, setOpen] = useState(false);
  // const [options, setOptions] = useState([]);
  const [selectedValue, setSelectedValue] = useState<EnumOption | null>(null);
  const [inputValue, setInputValue] = useState('');
  const dispatch = useDispatch();

  const fetchSecrets = useCallback(
    debounce((value: string) => {
      if (value) {
        const payload: any = { search_item: value };
        dispatch(secretManagerActions.getSecretsRequest(payload));
      }
    }, 2000),
    []
  );

  useEffect(() => {
    if (inputValue.trim()) {
      fetchSecrets(inputValue);
    }
  }, [inputValue, fetchSecrets]);

  useEffect(() => {
    if (secretsList.length) {
      if (data) {
        const [sec, id] = data.split(':');
        const selected = secretsList.find((item: any) => item.secret_id === Number(id));
        if (selected) {
          setSelectedValue(selected || null);
        }
      } else {
        setSelectedValue(data);
      }
    }
  }, [secretsList.length, data]);

  const showDescription = !isDescriptionHidden(
    visible,
    description,
    focused,
    appliedUiSchemaOptions.showUnfocusedDescription
  );

  const firstFormHelperText = showDescription ? description : !isValid ? errors : null;
  const secondFormHelperText = showDescription && !isValid ? errors : null;

  if (!visible) {
    return null;
  }

  return (
    <>
      <Autocomplete
        open={open}
        disabled={!enabled}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onInputChange={(event, value, reason) => {
          if (reason === 'input') {
            setInputValue(value);
          }
        }}
        value={selectedValue}
        onChange={(event: React.SyntheticEvent, newValue: EnumOption | null) => {
          setSelectedValue(newValue);
          handleChange(path, newValue ? `secret:${newValue?.value}` : null);
        }}
        options={secretsList.length ? secretsList : options}
        loading={loadingSecrets}
        getOptionLabel={(option: any) => option?.secret_name || ''}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              label={label}
              type='text'
              // inputProps={get(params, 'inputProps', {})}
              inputRef={params.InputProps.ref}
              autoFocus={appliedUiSchemaOptions.focus}
              // disabled={!enabled || false}
              id={id}
              required={required && !appliedUiSchemaOptions.hideRequiredAsterisk}
              error={errors.length > 0}
              fullWidth={!appliedUiSchemaOptions.trim}
              InputLabelProps={data ? { shrink: true } : undefined}
              onFocus={onFocus}
              onBlur={onBlur}
              focused={focused}
            />
          );
        }}
        renderOption={renderOption}
        filterOptions={filterOptions}
      />
      <FormHelperText error={!isValid && !showDescription}>{firstFormHelperText}</FormHelperText>
      <FormHelperText error={!isValid}>{secondFormHelperText}</FormHelperText>
    </>
  );
};

const mapStateToProps = (state: any) => ({
  secretsList: state.secretsManager.secretsData.map((item: any) => {
    return { ...item, label: item.secret_name, value: item.secret_id };
  }),
  loadingSecrets: state.secretsManager.isSecretManagementDataLoading,
});

export default connect(mapStateToProps)(MuiAutocomplete);
